/**
 * The care record: the score that matters. Days of continuous care, best
 * streak, counters, and a 7-day care-quality estimate. Pure data + rules; the
 * sync layer stores it, the UI shows it.
 *
 * A "day" is the local calendar day. A day counts as cared-for when the dog
 * was fed, played with, or otherwise looked after during it.
 */
export interface CareState {
  /** local calendar days (YYYY-MM-DD) that counted, most recent last; capped */
  days: string[];
  bestStreak: number;
  emptyBowl: number;
  fetches: number;
  tricksTaught: number;
  visitorsChased: number;
  feeds: number;
  scoops?: number;
  cans?: number;
  holes?: number;
  gameBest?: number;
  /** games: trick routines done, squirrel races won, longest fetch (m, one decimal) */
  routines?: number;
  squirrelWins?: number;
  bestFetch?: number;
  /** in-air catches */
  catches?: number;
  /** agility course: runs, and the best points */
  agilityRuns?: number;
  agilityBest?: number;
  /** the day she came to this household (YYYY-MM-DD); the anniversary clock */
  togetherSince?: string;
  /** seconds of quality time per device (app open, her checked out to you) */
  qualityTime?: Record<string, number>;
  /** last 7 days' quality samples 0..1 keyed by day */
  quality: Record<string, number>;
  /** credits: two ever-growing counters (they merge by max like everything else); balance = earned − spent */
  earned?: number;
  spent?: number;
  /** the last day a missed-day penalty was charged through */
  penalisedThrough?: string;
  updatedAt: number;
}

/** what things earn (per event) and cost; a cared-for day is the big one */
export const EARN: Partial<Record<CareEvent, number>> = { feed: 2, play: 1, fetch: 1, trick: 3, visitor: 2, groom: 2, scoop: 1, can: 2, hole: 5, routine: 6, squirrelWin: 4, longThrow: 3, catch: 4, agility: 5, agilityBest: 5 };
const WHY: Partial<Record<CareEvent, string>> = { feed: 'feeding her', play: 'playing with her', fetch: 'a fetch', trick: 'a trick', visitor: 'seeing off a visitor', groom: 'grooming', scoop: 'scooping', can: 'a can down', hole: 'in the hole!', routine: 'a trick routine!', squirrelWin: 'she caught up with the squirrel!', longThrow: 'a new longest fetch!', catch: 'caught in the air!', agility: 'a clean run', agilityBest: 'a new agility best!' };
export const EARN_DAY = 10;
export const LOSE_EMPTY_BOWL = 5;
export const LOSE_MISSED_DAY = 5;
export const PRICES = { kibble: 10, treats: 6, torch: 20, brush: 3, bath: 8, vet: 15 } as const;

export type CareEvent = 'feed' | 'play' | 'fetch' | 'trick' | 'visitor' | 'emptyBowl' | 'open' | 'groom' | 'vet' | 'scoop' | 'can' | 'hole' | 'routine' | 'squirrelWin' | 'longThrow' | 'catch' | 'agility' | 'agilityBest';

export function today(d = new Date()): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function dayOffset(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return today(new Date(y, m - 1, d + delta));
}

export function emptyCare(): CareState {
  return { days: [], bestStreak: 0, emptyBowl: 0, fetches: 0, tricksTaught: 0, visitorsChased: 0, feeds: 0, quality: {}, updatedAt: 0 };
}

export class CareRecord {
  state: CareState;
  onChange?: (s: CareState) => void;
  /** credits changed: +/− amount and why (for a HUD toast) */
  onCredit?: (delta: number, why: string) => void;

  /** first time credits are looked at, grant a balance for what's already been done */
  private ensureCredits(): void {
    const s = this.state;
    if (s.earned !== undefined) return;
    s.earned = 30 + s.feeds * 2 + s.fetches + s.tricksTaught * 3 + (s.cans ?? 0) * 2 + (s.holes ?? 0) * 5 + s.days.length * EARN_DAY;
    s.spent = 0;
  }

  get credits(): number {
    this.ensureCredits();
    return (this.state.earned ?? 0) - (this.state.spent ?? 0);
  }

  earn(n: number, why: string): void {
    this.ensureCredits();
    this.state.earned = (this.state.earned ?? 0) + n;
    this.state.updatedAt = Date.now();
    this.onCredit?.(n, why);
    this.onChange?.(this.state);
  }

  /** pay; false (and nothing charged) if it can't be afforded, unless `debt` lets it go negative */
  spend(n: number, why: string, debt = false): boolean {
    this.ensureCredits();
    if (!debt && this.credits < n) return false;
    this.state.spent = (this.state.spent ?? 0) + n;
    this.state.updatedAt = Date.now();
    this.onCredit?.(-n, why);
    this.onChange?.(this.state);
    return true;
  }

  constructor(state?: CareState) {
    this.state = state ?? emptyCare();
  }

  /** consecutive cared-for days ending today or yesterday (a streak survives until a day is missed) */
  get currentStreak(): number {
    const days = new Set(this.state.days);
    let d = today();
    if (!days.has(d)) d = dayOffset(d, -1);
    let n = 0;
    while (days.has(d)) {
      n++;
      d = dayOffset(d, -1);
    }
    return n;
  }

  /** total quality time across every phone, seconds */
  get qualitySeconds(): number {
    return Object.values(this.state.qualityTime ?? {}).reduce((a, b) => a + b, 0);
  }

  /** add seconds of quality time for this device (saved by the caller) */
  addQualityTime(device: string, seconds: number): void {
    const q = (this.state.qualityTime ??= {});
    q[device] = (q[device] ?? 0) + seconds;
  }

  get totalDays(): number {
    return this.state.days.length;
  }

  /** 0..1 rolling quality over the last 7 days (or fewer, if care started more recently); missed days count as 0 */
  get quality(): number {
    const first = this.state.days[0] ?? today();
    let sum = 0;
    let n = 0;
    let d = today();
    for (let i = 0; i < 7; i++) {
      if (d < first) break;
      sum += this.state.quality[d] ?? 0;
      n++;
      d = dayOffset(d, -1);
    }
    return n ? sum / n : 0;
  }

  get qualityWord(): string {
    const q = this.quality;
    return q > 0.8 ? 'thriving' : q > 0.55 ? 'happy' : q > 0.3 ? 'fine' : q > 0.1 ? 'lonely' : 'neglected';
  }

  /** the same scale as a face */
  get moodFace(): string {
    const q = this.quality;
    return q > 0.8 ? '😊' : q > 0.55 ? '🙂' : q > 0.3 ? '😐' : q > 0.1 ? '😢' : '😠';
  }

  /** record something that happened; `fedToday`/`playedToday` feed the quality sample */
  record(ev: CareEvent, ctx: { hunger: number; bowlFill: number }): void {
    const s = this.state;
    const d = today();
    if (!s.togetherSince) s.togetherSince = s.days[0] ?? d;
    switch (ev) {
      case 'feed':
        s.feeds++;
        break;
      case 'fetch':
        s.fetches++;
        break;
      case 'trick':
        s.tricksTaught++;
        break;
      case 'visitor':
        s.visitorsChased++;
        break;
      case 'emptyBowl':
        s.emptyBowl++;
        break;
      case 'scoop':
        s.scoops = (s.scoops ?? 0) + 1;
        break;
      case 'can':
        s.cans = (s.cans ?? 0) + 1;
        break;
      case 'hole':
        s.holes = (s.holes ?? 0) + 1;
        break;
      case 'routine':
        s.routines = (s.routines ?? 0) + 1;
        break;
      case 'squirrelWin':
        s.squirrelWins = (s.squirrelWins ?? 0) + 1;
        break;
      case 'longThrow':
        break;
      case 'catch':
        s.catches = (s.catches ?? 0) + 1;
        break;
      case 'agility':
        s.agilityRuns = (s.agilityRuns ?? 0) + 1;
        break;
      case 'agilityBest':
        break;
      case 'play':
      case 'open':
      case 'groom':
      case 'vet':
        break;
    }
    // credits: every bit of care earns a little; an empty bowl costs; missed days cost on the next open
    this.ensureCredits();
    const gain = EARN[ev];
    if (gain) this.earn(gain, WHY[ev] ?? ev);
    if (ev === 'emptyBowl') this.spend(LOSE_EMPTY_BOWL, 'the bowl ran empty', true);
    if (ev === 'open') {
      const last = s.days[s.days.length - 1];
      if (last && last < dayOffset(d, -1)) {
        const from = s.penalisedThrough && s.penalisedThrough > last ? s.penalisedThrough : last;
        let missed = 0;
        for (let x = dayOffset(from, 1); x < d && missed < 7; x = dayOffset(x, 1)) missed++;
        if (missed > 0) this.spend(LOSE_MISSED_DAY * missed, `${missed} day${missed > 1 ? 's' : ''} without care`, true);
      }
      s.penalisedThrough = dayOffset(d, -1);
    }
    // a day counts once she has been fed or played with in it
    if (ev !== 'open' && ev !== 'emptyBowl' && !s.days.includes(d)) {
      s.days.push(d);
      if (s.days.length > 400) s.days.splice(0, s.days.length - 400);
      s.bestStreak = Math.max(s.bestStreak, this.currentStreak);
      this.earn(EARN_DAY, 'a cared-for day');
    }
    // quality sample: fed (not hungry, bowl has food) + interaction; ratchets up within the day
    const fed = ctx.hunger < 0.6 && ctx.bowlFill > 0.05 ? 0.6 : ctx.hunger < 0.9 ? 0.3 : 0;
    const played = ev === 'play' || ev === 'fetch' || ev === 'trick' || ev === 'groom' || ev === 'scoop' || ev === 'can' || ev === 'hole' || ev === 'routine' || ev === 'squirrelWin' || ev === 'longThrow' || ev === 'catch' || ev === 'agility' ? 0.4 : 0;
    const prev = s.quality[d] ?? 0;
    s.quality[d] = Math.min(1, Math.max(prev, fed + played, prev + played * 0.5));
    for (const k of Object.keys(s.quality)) if (k < dayOffset(d, -8)) delete s.quality[k];
    s.updatedAt = Date.now();
    this.onChange?.(s);
  }

  /** a fetch she brought back from `metres` away; true if it's the longest ever */
  noteFetch(metres: number): boolean {
    const m = Math.round(metres * 10) / 10;
    if (m <= (this.state.bestFetch ?? 0)) return false;
    this.state.bestFetch = m;
    this.state.updatedAt = Date.now();
    this.onChange?.(this.state);
    return true;
  }

  /** an agility run scored `points`; true if it's the best yet */
  noteAgility(points: number): boolean {
    if (points <= (this.state.agilityBest ?? 0)) return false;
    this.state.agilityBest = points;
    this.state.updatedAt = Date.now();
    this.onChange?.(this.state);
    return true;
  }

  /** merge a copy from another device: union of days, max of counters/quality */
  merge(other: CareState): void {
    const s = this.state;
    s.days = [...new Set([...s.days, ...other.days])].sort();
    s.bestStreak = Math.max(s.bestStreak, other.bestStreak, this.currentStreak);
    for (const k of ['emptyBowl', 'fetches', 'tricksTaught', 'visitorsChased', 'feeds'] as const) s[k] = Math.max(s[k], other[k]);
    s.scoops = Math.max(s.scoops ?? 0, other.scoops ?? 0);
    s.cans = Math.max(s.cans ?? 0, other.cans ?? 0);
    s.holes = Math.max(s.holes ?? 0, other.holes ?? 0);
    s.gameBest = Math.max(s.gameBest ?? 0, other.gameBest ?? 0);
    s.routines = Math.max(s.routines ?? 0, other.routines ?? 0);
    s.squirrelWins = Math.max(s.squirrelWins ?? 0, other.squirrelWins ?? 0);
    s.bestFetch = Math.max(s.bestFetch ?? 0, other.bestFetch ?? 0);
    s.catches = Math.max(s.catches ?? 0, other.catches ?? 0);
    s.agilityRuns = Math.max(s.agilityRuns ?? 0, other.agilityRuns ?? 0);
    s.agilityBest = Math.max(s.agilityBest ?? 0, other.agilityBest ?? 0);
    if (other.earned !== undefined || s.earned !== undefined) {
      s.earned = Math.max(s.earned ?? 0, other.earned ?? 0);
      s.spent = Math.max(s.spent ?? 0, other.spent ?? 0);
    }
    if (other.penalisedThrough && (!s.penalisedThrough || other.penalisedThrough > s.penalisedThrough)) s.penalisedThrough = other.penalisedThrough;
    const q = (s.qualityTime ??= {});
    for (const [k, v] of Object.entries(other.qualityTime ?? {})) q[k] = Math.max(q[k] ?? 0, v);
    const mine = s.togetherSince ?? s.days[0];
    const theirs = other.togetherSince ?? other.days[0];
    s.togetherSince = [mine, theirs].filter(Boolean).sort()[0];
    for (const [k, v] of Object.entries(other.quality)) s.quality[k] = Math.max(s.quality[k] ?? 0, v);
    s.updatedAt = Math.max(s.updatedAt, other.updatedAt);
    this.onChange?.(s);
  }
}
